import React, { useContext, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useNavigate } from 'react-router-dom';

import useAcompanhamentoState from '../../hooks/useAcompanhamentoState';
import MyLoading from '../../components/myLoading';
import MyMapModal from '../../components/myMapModal';
import { AuthContext } from '../../contexts/auth';
import TELAS from '../../constants/telas';
import TELA_ACOES from '../../constants/telaAcoes';
import permissaoTelas from '../../utils/permissaoTelas';

const PAGINA = {
  // A tela pertence visualmente à Gestão, mas sua autorização acompanha Tarefas.
  aplicacao: 'SisPalo - Tarefas',
  funcionalidade: 'Tarefas',
  tela: TELAS.TAREFAS,
  acoes: [TELA_ACOES.Consultar],
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
};

const timestamp = (value) => {
  if (!value) return 0;
  const [datePart, timePart = '00:00:00'] = value.split(' ');
  const [day, month, year] = datePart.split('/');
  const parsed = new Date(`${year}-${month}-${day}T${timePart}`).getTime();
  return Number.isNaN(parsed) ? new Date(value).getTime() || 0 : parsed;
};

const formatarPermanencia = (entrada, saida) => {
  if (!entrada || !saida) return null;
  const diferenca = timestamp(saida) - timestamp(entrada);
  if (diferenca < 0) return null;

  const minutosTotais = Math.floor(diferenca / 60000);
  const dias = Math.floor(minutosTotais / 1440);
  const horas = Math.floor((minutosTotais % 1440) / 60);
  const minutos = minutosTotais % 60;

  return [
    dias ? `${dias}d` : null,
    horas ? `${horas}h` : null,
    `${minutos}min`,
  ].filter(Boolean).join(' ');
};

const getInitials = (name = '') => name
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const Info = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
    <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.2 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" lineHeight={1}>{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        fontSize="0.78rem"
        lineHeight={1.25}
        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        title={value || '-'}
      >
        {value || '-'}
      </Typography>
    </Box>
  </Box>
);

const CardFiltro = ({ ativo, cor, label, quantidade, onClick }) => {
  const cores = {
    primary: { principal: '#3f6694', fundo: '#edf3fa' },
    error: { principal: '#bd4545', fundo: '#fff0f0' },
    success: { principal: '#2f7d50', fundo: '#edf8f1' },
  };
  const estilo = cores[cor] || cores.primary;

  return (
    <ButtonBase onClick={onClick} aria-pressed={ativo} sx={{ borderRadius: 1.5, textAlign: 'left' }}>
      <Paper variant="outlined" sx={{
        px: 1.5, py: 0.65, minWidth: 135, width: '100%', borderRadius: 1.5,
        borderWidth: ativo ? 2 : 1,
        borderColor: ativo ? estilo.principal : `${estilo.principal}55`,
        borderTop: `3px solid ${estilo.principal}`,
        bgcolor: ativo ? estilo.fundo : 'background.paper',
        boxShadow: ativo ? `0 3px 10px ${estilo.principal}20` : 'none',
        transition: 'border-color .2s, background-color .2s, transform .2s, box-shadow .2s',
        '&:hover': { bgcolor: estilo.fundo, transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${estilo.principal}20` },
      }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
        <Typography variant="h6" sx={{ color: estilo.principal }} fontWeight={800}>{quantidade}</Typography>
      </Paper>
    </ButtonBase>
  );
};

const AcompCoordenador = () => {
  const navigate = useNavigate();
  const { permissaoUsuario } = useContext(AuthContext);
  const states = useAcompanhamentoState();
  const [syncTime, setSyncTime] = useState(new Date());
  const [mapa, setMapa] = useState({ open: false, latitude: null, longitude: null });
  const [filtro, setFiltro] = useState('todos');
  const [pesquisaUsuario, setPesquisaUsuario] = useState('');
  const [pesquisaTarefa, setPesquisaTarefa] = useState('');

  useEffect(() => {
    if (!permissaoTelas(permissaoUsuario, TELA_ACOES.Consultar, PAGINA)) {
      navigate('/gestor/dashboard');
      return undefined;
    }

    const loadData = () => {
      states.submit.set();
      setSyncTime(new Date());
    };
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, []);

  const usuarios = [...states.tarefa.value].sort(
    (a, b) => timestamp(b.entrada) - timestamp(a.entrada)
  );
  const emTarefa = usuarios.filter((item) => !item.saida).length;
  const finalizados = usuarios.length - emTarefa;
  const normalizar = (valor = '') => valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const usuarioPesquisado = normalizar(pesquisaUsuario.trim());
  const tarefaPesquisada = normalizar(pesquisaTarefa.trim());
  const usuariosFiltrados = usuarios.filter((item) => {
    const correspondeStatus = filtro === 'emTarefa'
      ? !item.saida
      : filtro === 'finalizados'
        ? Boolean(item.saida)
        : true;
    const correspondeUsuario = !usuarioPesquisado
      || normalizar(item.usuario).includes(usuarioPesquisado);
    const correspondeTarefa = !tarefaPesquisada
      || normalizar(item.tipo || 'Check-in').includes(tarefaPesquisada);
    return correspondeStatus && correspondeUsuario && correspondeTarefa;
  });

  return (
    <Box sx={{ width: '100%' }}>
      <MyLoading render={states.submit.value} />

      <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ borderLeft: '4px solid #3f6694', pl: 1.25 }}>
          <Typography variant="h6" fontWeight={700}>Acompanhamento de usuários</Typography>
          <Typography variant="caption" color="text.secondary">
            Visualize onde cada usuário está e qual tarefa está executando.
          </Typography>
        </Box>
        <Button color="warning" variant="contained" size="small" startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/gestor/dashboard')}>
          Voltar
        </Button>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{
          mb: 1, p: 1, alignItems: { sm: 'center' }, flexWrap: 'wrap', rowGap: 1,
          bgcolor: 'background.paper', border: '1px solid', borderColor: '#d4dce8',
          borderRadius: 2, boxShadow: '0 3px 10px rgba(43,65,96,.06)',
        }}
      >
        <CardFiltro ativo={filtro === 'todos'} cor="primary" label="Usuários localizados"
          quantidade={usuarios.length} onClick={() => setFiltro('todos')} />
        <CardFiltro ativo={filtro === 'emTarefa'} cor="error" label="Em tarefa"
          quantidade={emTarefa} onClick={() => setFiltro('emTarefa')} />
        <CardFiltro ativo={filtro === 'finalizados'} cor="success" label="Finalizados"
          quantidade={finalizados} onClick={() => setFiltro('finalizados')} />
        <TextField
          size="small"
          type="search"
          label="Pesquisar usuário"
          placeholder="Digite o nome do usuário"
          value={pesquisaUsuario}
          onChange={(event) => setPesquisaUsuario(event.target.value)}
          sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
        />
        <TextField
          size="small"
          type="search"
          label="Pesquisar tarefa"
          placeholder="Digite o nome da tarefa"
          value={pesquisaTarefa}
          onChange={(event) => setPesquisaTarefa(event.target.value)}
          sx={{ width: { xs: '100%', sm: 250 }, flexShrink: 0 }}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
        />
        <Box sx={{
          textAlign: { xs: 'left', sm: 'right' },
          alignSelf: 'center',
          ml: { sm: 'auto !important' },
          flexShrink: 0,
        }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Período: {formatDate(states.dataInicial.value)} a {formatDate(states.dataFinal.value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Última atualização: {syncTime.toLocaleString('pt-BR')}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 1 }} />

      {usuariosFiltrados.length === 0 && !states.submit.value ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            bgcolor: 'background.default',
            borderStyle: 'dashed',
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            Nenhum usuário encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Tente alterar o status selecionado ou limpar os campos de pesquisa.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.25}>
          {usuariosFiltrados.map((usuario) => {
            const ativo = !usuario.saida;
            const status = ativo ? 'Em tarefa' : 'Finalizada';
            const corStatus = ativo ? 'error' : 'success';
            const permanencia = formatarPermanencia(usuario.entrada, usuario.saida);
            const possuiCoordenadas = usuario.latitude != null && usuario.longitude != null;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={usuario.id}>
                <Paper variant="outlined" sx={{
                  p: 1.25,
                  height: '100%',
                  borderTop: 3,
                  borderTopColor: `${corStatus}.main`,
                  bgcolor: ativo ? '#fff1f1' : '#f1f8f3',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar src={usuario.foto || undefined} alt={usuario.usuario} sx={{ width: 38, height: 38, fontSize: '0.85rem' }}>
                      {getInitials(usuario.usuario)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap title={usuario.usuario}>{usuario.usuario}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Chip size="small" color={corStatus} label={status}
                          sx={{ height: 20, fontSize: '0.68rem' }} />
                        {permanencia && (
                          <Chip size="small" variant="outlined" label={permanencia}
                            title="Tempo de permanência no local"
                            sx={{ height: 20, fontSize: '0.68rem' }} />
                        )}
                      </Stack>
                    </Box>
                    <Button
                      aria-label="Ver localização no mapa"
                      disabled={!possuiCoordenadas}
                      onClick={() => setMapa({ open: true, latitude: usuario.latitude, longitude: usuario.longitude })}
                      sx={{ minWidth: 32, p: 0.5 }}
                    >
                      <FmdGoodIcon />
                    </Button>
                  </Box>

                  <Stack spacing={0.75}>
                    <Info icon={<PlaceIcon fontSize="small" />} label="Posto" value={usuario.posto} />
                    <Info icon={<FmdGoodIcon fontSize="small" />} label="Local atual" value={usuario.nome} />
                    <Info
                      icon={<AssignmentTurnedInIcon fontSize="small" />}
                      label="Tarefa"
                      value={String(usuario.tipo || '').trim() && usuario.tipo !== '-' ? usuario.tipo : 'Check-in'}
                    />
                    <Info
                      icon={<AccessTimeIcon fontSize="small" />}
                      label="Horário"
                      value={`Entrada: ${usuario.entrada || '-'} | Saída: ${usuario.saida || '-'}`}
                    />
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <MyMapModal mapa={{ set: setMapa, value: mapa }} />
    </Box>
  );
};

export default AcompCoordenador;
